import { isDesktop } from '@/consts/device';
import getFile from '@/requests/cloud-storage/getFile';
import { getAppStore, setAppStore } from '@/store';
import { RightPanelTab } from '@/store/navigation/types';
import { driveFileByIdToContentFile } from '@/utils/media/driveFileToContentFile';
import { Screen } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type DriveFileDeeplink = Extract<Deeplink, { kind: DeeplinkKind.DriveFile }>;

/**
 * `file_uid` deeplink: open a generation straight from its Drive file.
 *
 * The sibling of `taskHandler`, one step shorter. A `task_id` names a
 * Magic-Flow job, so that handler resolves the job first and only then reads
 * the Drive file behind it for a name; a `file_uid` names the stored result
 * outright, so there is no job to resolve — which also means it keeps working
 * for a generation old enough that its task has aged out of the flow API.
 *
 * Routes to the same place by the same store writes, so a result opened either
 * way lands in an identical lightbox. It shares `driveFileToContentFile`'s
 * attribute reading and media-type logic, so an entry arrived at by deeplink is
 * indistinguishable from the same entry scrolled to in the list.
 *
 * `resultFile` is deliberately not set: that carries a `FlowMedia` (the job's
 * output descriptor), which a Drive file alone cannot reconstruct. The lightbox
 * opens from `pendingLightboxFileId` against the prepended content file, which
 * is the path the creations grid uses too.
 */
const driveFileHandler = async ({
  fileUid,
}: DriveFileDeeplink): Promise<void> => {
  setAppStore({ screen: Screen.Loading, hasAutoSelectedPreset: true });

  try {
    const file = await getFile({ uid: fileUid });
    if (!file) {
      setAppStore({ screen: Screen.Library, hasAutoSelectedPreset: false });
      return;
    }

    const contentFile = driveFileByIdToContentFile(file);

    setAppStore({
      screen: Screen.Files,
      ...(isDesktop && { rightPanelTab: RightPanelTab.Creations }),
      pendingLightboxFileId: contentFile.uuid,
      hasAutoSelectedPreset: false,
      isResultMode: true,
    });

    getAppStore().prependContentFile(contentFile);
  } catch {
    setAppStore({ screen: Screen.Library, hasAutoSelectedPreset: false });
  }
};

export default driveFileHandler;
